'use client';

import { createRoot, type Root } from 'react-dom/client';

import { createWidgetMount } from '@/shared/utils/widget-mount';

import { CartButtonWidget, type CartButtonVariant } from '../components/CartButtonWidget';

const OBSERVED_ATTRIBUTES = ['variant', 'in-cart', 'idle-label', 'pay-label', 'disabled'];

function readBooleanAttribute(element: HTMLElement, name: string): boolean {
  const value = element.getAttribute(name);
  return value !== null && value !== 'false';
}

function readVariant(element: HTMLElement): CartButtonVariant {
  return element.getAttribute('variant') === 'icon' ? 'icon' : 'wide';
}

export class CartButtonWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this, {
        rootAttribute: 'data-adipa-cart-button-root',
      }) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const variant = readVariant(this);
    const inCart = readBooleanAttribute(this, 'in-cart');
    const disabled = readBooleanAttribute(this, 'disabled');
    const idleLabel = this.getAttribute('idle-label') ?? undefined;
    const payLabel = this.getAttribute('pay-label') ?? undefined;

    this.root.render(
      <CartButtonWidget
        disabled={disabled}
        idleLabel={idleLabel}
        inCart={inCart}
        payLabel={payLabel}
        variant={variant}
        onAddToCart={() => {
          this.dispatchEvent(
            new CustomEvent('adipa-cart-add', { bubbles: true, composed: true }),
          );
        }}
        onCheckout={() => {
          this.dispatchEvent(
            new CustomEvent('adipa-cart-checkout', { bubbles: true, composed: true }),
          );
        }}
      />,
    );
  }
}
